import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Eye, Code, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';

export default function EmailTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getEmailTemplates();
      setTemplates(response.data.items);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetTemplates = async () => {
    if (!confirm('Êtes-vous sûr de vouloir réinitialiser les templates ? Cela remplacera les templates existants par les versions par défaut.')) {
      return;
    }
    
    setResetting(true);
    try {
      await adminAPI.resetEmailTemplates();
      toast.success('Templates réinitialisés avec succès');
      loadTemplates();
    } catch (error) {
      console.error('Error resetting templates:', error);
      toast.error('Erreur lors de la réinitialisation');
    } finally {
      setResetting(false);
    }
  };

  const openPreview = (template) => {
    setSelectedTemplate(template);
    setPreviewOpen(true);
  };

  const flowTypeLabels = {
    WELCOME: 'Bienvenue',
    ABANDONED_CART_1: 'Panier abandonné #1',
    ABANDONED_CART_2: 'Panier abandonné #2',
    ABANDONED_CART_3: 'Panier abandonné #3',
    ORDER_CONFIRMED: 'Confirmation commande',
    ORDER_SHIPPED: 'Expédition',
    REVIEW_REQUEST: 'Demande avis',
    PROMO_FLASH: 'Promo Flash'
  };

  return (
    <div className="space-y-6" data-testid="admin-emails">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Templates Emails</h1>
          <p className="text-muted-foreground">Gérez vos templates d'emails</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleResetTemplates}
            disabled={resetting || loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${resetting ? 'animate-spin' : ''}`} />
            {resetting ? 'Réinitialisation...' : 'Réinitialiser'}
          </Button>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Nouveau template
          </Button>
        </div>
      </div>

      <Tabs defaultValue="templates">
        <TabsList>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="flows">Flows Automation</TabsTrigger>
          <TabsTrigger value="campaigns">Campagnes</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="mt-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-48 bg-secondary animate-pulse rounded-lg" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template, index) => (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-base">{template.name}</CardTitle>
                          <p className="text-xs font-mono text-muted-foreground mt-1">
                            {template.key}
                          </p>
                        </div>
                        <Badge variant={template.is_active ? 'default' : 'secondary'}>
                          {template.is_active ? 'Actif' : 'Inactif'}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {template.flow_type && (
                        <Badge variant="outline" className="mb-3">
                          {flowTypeLabels[template.flow_type] || template.flow_type}
                        </Badge>
                      )}
                      
                      <div className="space-y-2 text-sm">
                        <div>
                          <p className="text-muted-foreground">Sujet (FR)</p>
                          <p className="truncate">{template.subject?.fr}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Sujet (EN)</p>
                          <p className="truncate">{template.subject?.en}</p>
                        </div>
                      </div>

                      <div className="flex gap-2 mt-4">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => openPreview(template)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Aperçu
                        </Button>
                        <Button variant="outline" size="sm" className="flex-1">
                          <Edit className="h-4 w-4 mr-1" />
                          Éditer
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="flows" className="mt-6">
          <Card>
            <CardContent className="p-6">
              <div className="text-center py-8">
                <Code className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">Flows Automation</h3>
                <p className="text-muted-foreground text-sm max-w-md mx-auto">
                  Configurez des séquences d'emails automatiques : panier abandonné, 
                  post-achat, bienvenue, etc.
                </p>
                <Button className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Créer un flow
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="campaigns" className="mt-6">
          <Card>
            <CardContent className="p-6">
              <div className="text-center py-8">
                <Code className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">Campagnes Email</h3>
                <p className="text-muted-foreground text-sm max-w-md mx-auto">
                  Créez et planifiez des campagnes email ponctuelles : 
                  promotions, nouveautés, newsletters.
                </p>
                <Button className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Nouvelle campagne
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{selectedTemplate?.name}</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="fr">
            <TabsList>
              <TabsTrigger value="fr">Français</TabsTrigger>
              <TabsTrigger value="en">English</TabsTrigger>
            </TabsList>
            <TabsContent value="fr" className="mt-4">
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-secondary p-3 border-b">
                  <p className="text-sm">
                    <strong>Sujet:</strong> {selectedTemplate?.subject?.fr}
                  </p>
                </div>
                <div 
                  className="p-4 bg-white"
                  dangerouslySetInnerHTML={{ 
                    __html: selectedTemplate?.html_content?.fr || '' 
                  }}
                />
              </div>
            </TabsContent>
            <TabsContent value="en" className="mt-4">
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-secondary p-3 border-b">
                  <p className="text-sm">
                    <strong>Subject:</strong> {selectedTemplate?.subject?.en}
                  </p>
                </div>
                <div 
                  className="p-4 bg-white"
                  dangerouslySetInnerHTML={{ 
                    __html: selectedTemplate?.html_content?.en || '' 
                  }}
                />
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
